import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, ChevronLeft, ArrowLeft, CheckCircle, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { toast } from "sonner";

const buildStatusBadges = (t) => ({
    pendiente_revision: { label: t("Pendiente de revisión", "Pending review"), color: "bg-amber-500/20 text-amber-300" },
    aprobada: { label: t("Aprobada", "Approved"), color: "bg-blue-500/20 text-blue-300" },
    etiqueta_generada: { label: t("Etiqueta generada", "Label generated"), color: "bg-emerald-500/20 text-emerald-300" },
    enviada_cliente: { label: t("Etiqueta enviada", "Label sent"), color: "bg-emerald-500/20 text-emerald-300" },
    completada: { label: t("Completada", "Completed"), color: "bg-emerald-500/20 text-emerald-300" },
    rechazada: { label: t("Rechazada", "Rejected"), color: "bg-rose-500/20 text-rose-300" },
});

export default function Returns() {
    const { user, loading } = useAuth();
    const { t, lang } = useLanguage();
    const STATUS_BADGES = buildStatusBadges(t);
    const nav = useNavigate();
    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);
    const [creating, setCreating] = useState(null); // {order_id, country, ...}
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);

    const load = async () => {
        try {
            const [ord, mine] = await Promise.all([
                api.get("/orders/mine"),
                api.get("/returns/mine"),
            ]);
            setOrders((ord.data || []).filter((o) => o.payment_status === "paid"));
            setReturns(mine.data || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

    if (!user) return null;

    const startReturn = (order) => {
        setCreating({
            order_id: order._id,
            country: order.country || "SV",
            reason: "",
            items_description: "",
            customer_phone: order.customer_phone || "",
            address_street: (order.shipping_address || "").split("\n")[0] || "",
            address_apt: "",
            address_city: order.us_city || "",
            address_state: order.us_state || "",
            address_zip: order.us_zip || "",
            departamento_id: order.departamento_id || "",
            municipio_id: order.municipio_id || "",
            address_reference: order.sv_reference || "",
        });
    };

    const submit = async () => {
        if (!creating) return;
        if ((creating.reason || "").trim().length < 10) {
            toast.error(t("La razón debe tener al menos 10 caracteres", "The reason must be at least 10 characters")); return;
        }
        setSubmitting(true);
        try {
            const payload = {
                order_id: creating.order_id, reason: creating.reason,
                items_description: creating.items_description, customer_phone: creating.customer_phone,
                address_street: creating.address_street, address_apt: creating.address_apt,
                address_city: creating.address_city, address_state: creating.address_state, address_zip: creating.address_zip,
                departamento_id: creating.departamento_id ? Number(creating.departamento_id) : null,
                municipio_id: creating.municipio_id ? Number(creating.municipio_id) : null,
                address_reference: creating.address_reference,
            };
            await api.post("/returns", payload);
            toast.success(t("Devolución enviada — el admin la revisará pronto", "Return submitted — the admin will review it shortly"));
            setCreating(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("Error al enviar", "Error submitting"));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-10">
            <button onClick={() => nav(-1)} className="text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] inline-flex items-center gap-1 mb-4">
                <ArrowLeft className="w-3 h-3" /> {t("Volver", "Back")}
            </button>
            <h1 className="font-display text-4xl mb-2 flex items-center gap-3"><Package className="w-8 h-8 text-[var(--tb-gold)]" /> {t("Devoluciones", "Returns")}</h1>
            <p className="text-sm text-[var(--tb-text-2)] mb-8">{t("Inicia una devolución de un pedido pagado. El admin revisará tu solicitud y te enviará una etiqueta de retorno por correo.", "Start a return for a paid order. The admin will review your request and email you a return label.")}</p>

            {creating ? (
                <div className="tb-card p-6 space-y-3" data-testid="return-form">
                    <h3 className="font-display text-xl mb-1">{t("Nueva devolución", "New return")}</h3>
                    <p className="text-[10px] text-[var(--tb-muted)]">{t("Pedido", "Order")} #{creating.order_id.slice(-8)} · {creating.country === "US" ? "🇺🇸" : "🇸🇻"}</p>
                    <div>
                        <label className="tb-overline">{t("Razón de la devolución *", "Reason for the return *")}</label>
                        <textarea className="tb-input min-h-[80px]" placeholder={t("Ej. El producto llegó dañado / no es lo esperado...", "E.g. The product arrived damaged / not as expected...")}
                            value={creating.reason} onChange={(e) => setCreating({ ...creating, reason: e.target.value })}
                            data-testid="ret-reason" />
                    </div>
                    <div>
                        <label className="tb-overline">{t("Descripción de los artículos a devolver", "Description of items to return")}</label>
                        <textarea className="tb-input min-h-[60px]" placeholder={t("Qué piezas y en qué condición están...", "Which pieces and their condition...")}
                            value={creating.items_description} onChange={(e) => setCreating({ ...creating, items_description: e.target.value })}
                            data-testid="ret-items" />
                    </div>
                    <div>
                        <label className="tb-overline">{t("Teléfono de contacto", "Contact phone")}</label>
                        <input className="tb-input" value={creating.customer_phone} onChange={(e) => setCreating({ ...creating, customer_phone: e.target.value })} data-testid="ret-phone" />
                    </div>
                    <div className="pt-3 border-t border-[var(--tb-border)]">
                        <div className="text-xs font-medium text-[var(--tb-gold)] mb-2">{t("Dirección de recolección", "Pickup address")} {creating.country === "US" ? "(USA)" : "(El Salvador)"}</div>
                        <div className="space-y-2">
                            <input className="tb-input" placeholder={t("Calle / dirección", "Street / address")}
                                value={creating.address_street} onChange={(e) => setCreating({ ...creating, address_street: e.target.value })} data-testid="ret-street" />
                            <input className="tb-input" placeholder={creating.country === "US" ? t("Apt / Suite (opcional)", "Apt / Suite (optional)") : t("Referencia / punto de referencia", "Reference / landmark")}
                                value={creating.country === "US" ? creating.address_apt : creating.address_reference}
                                onChange={(e) => setCreating(creating.country === "US" ? { ...creating, address_apt: e.target.value } : { ...creating, address_reference: e.target.value })}
                                data-testid="ret-apt" />
                            {creating.country === "US" ? (
                                <div className="grid grid-cols-3 gap-2">
                                    <input className="tb-input" placeholder={t("Ciudad", "City")} value={creating.address_city} onChange={(e) => setCreating({ ...creating, address_city: e.target.value })} data-testid="ret-city" />
                                    <input className="tb-input uppercase" maxLength={2} placeholder={t("Estado", "State")} value={creating.address_state} onChange={(e) => setCreating({ ...creating, address_state: e.target.value.toUpperCase() })} data-testid="ret-state" />
                                    <input className="tb-input" placeholder="ZIP" value={creating.address_zip} onChange={(e) => setCreating({ ...creating, address_zip: e.target.value })} data-testid="ret-zip" />
                                </div>
                            ) : (
                                <p className="text-[10px] text-[var(--tb-muted)]">{t("Tu departamento y municipio se tomarán del pedido original. El admin puede ajustarlos.", "Your department and municipality will be taken from the original order. The admin can adjust them.")}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                        <button onClick={() => setCreating(null)} className="tb-btn-secondary !text-xs" data-testid="ret-cancel">{t("Cancelar", "Cancel")}</button>
                        <button onClick={submit} disabled={submitting} className="tb-btn-primary !text-xs disabled:opacity-50" data-testid="ret-submit">
                            {submitting ? t("Enviando...", "Sending...") : t("Enviar solicitud", "Send request")}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <section className="mb-10">
                        <h2 className="font-display text-xl mb-3">{t("Mis devoluciones", "My returns")} ({returns.length})</h2>
                        {returns.length === 0 ? (
                            <p className="text-xs text-[var(--tb-muted)]">{t("Aún no has solicitado devoluciones.", "You haven't requested any returns yet.")}</p>
                        ) : (
                            <div className="space-y-2">
                                {returns.map((r) => {
                                    const b = STATUS_BADGES[r.status] || STATUS_BADGES.pendiente_revision;
                                    return (
                                        <div key={r._id} className="tb-card p-4 flex items-start gap-3" data-testid={`return-row-${r._id}`}>
                                            <div className="w-10 h-10 rounded bg-[var(--tb-border-gold)]/15 flex items-center justify-center flex-shrink-0">
                                                {r.tracking_number ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <Clock className="w-5 h-5 text-[var(--tb-gold)]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-mono text-xs">{r._id}</span>
                                                    <span className={`tb-badge text-[10px] ${b.color}`}>{b.label}</span>
                                                    <span className="text-[10px] text-[var(--tb-muted)]">{t("Pedido", "Order")} #{(r.order_id || "").slice(-8)} · {r.country}</span>
                                                </div>
                                                <div className="text-sm mt-1 line-clamp-2">{r.reason}</div>
                                                {r.tracking_number && <div className="text-[10px] text-emerald-400 mt-1">{t("Tracking", "Tracking")}: {r.tracking_number} · {r.carrier}</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                    <section>
                        <h2 className="font-display text-xl mb-3">{t("Pedidos elegibles para devolución", "Orders eligible for return")}</h2>
                        {orders.length === 0 ? (
                            <p className="text-xs text-[var(--tb-muted)]">{t("No tienes pedidos pagados aún.", "You have no paid orders yet.")}</p>
                        ) : (
                            <div className="space-y-2">
                                {orders.map((o) => (
                                    <div key={o._id} className="tb-card p-4 flex items-center justify-between gap-3" data-testid={`elig-order-${o._id}`}>
                                        <div>
                                            <div className="text-sm font-medium">{t("Pedido", "Order")} #{o._id.slice(-8)} · {o.country}</div>
                                            <div className="text-[10px] text-[var(--tb-muted)]">${(o.total || 0).toFixed(2)} · {new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-US" : "es-SV")}</div>
                                        </div>
                                        <button onClick={() => startReturn(o)} className="tb-btn-primary !text-xs inline-flex items-center gap-1" data-testid={`start-return-${o._id}`}>
                                            <ChevronLeft className="w-3 h-3 rotate-180" /> {t("Iniciar devolución", "Start return")}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
